import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../contexts/PermissionContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { messageAPI } from '../../services/api'
import MessageCard from '../../components/messages/MessageCard'
import MessageCreateModal from '../../components/messages/MessageCreateModal'
import {
  HiOutlinePlusCircle,
  HiOutlineFunnel,
  HiOutlineMagnifyingGlass,
  HiOutlineChatBubbleLeftRight,
  HiOutlineXMark,
} from 'react-icons/hi2'
import { getSecurityLevelOptionsForRole } from '../../config/messageSecurity'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

export default function MessagesPage() {
  const { t } = useTranslation()
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth()
  const { userRole } = usePermissions()
  const { showSuccess, showError } = useAlerts()
  // eslint-disable-next-line no-unused-vars
  const { getEntity } = useWorkspace()

  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [audioPlayer, setAudioPlayer] = useState(null)
  const [playingMessageId, setPlayingMessageId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const [filters, setFilters] = useState({
    search: '', type: '', security_level: '', message_scope: '', is_read: '',
  })

  const securityLevelOptions = getSecurityLevelOptionsForRole(userRole)

  useEffect(() => {
    setPage(1)
    setMessages([])
    setHasMore(true)
    fetchMessages(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  async function fetchMessages(pageNum = 1, isInitial = false) {
    if (isInitial) setLoading(true)
    else setLoadingMore(true)
    try {
      const params = { page: pageNum, page_size: 10, ordering: '-created_at' }
      if (filters.search) params.search = filters.search
      if (filters.type) params.type = filters.type
      if (filters.security_level) params.security_level = filters.security_level
      if (filters.message_scope) params.message_scope = filters.message_scope
      if (filters.is_read) params.is_read = filters.is_read

      const response = await messageAPI.getMessages(params)
      if (response.success) {
        const data = response.data.data || response.data
        const messagesList = data.data || data.results || data
        const hasNextPage = data.next !== null || (data.pagination && data.pagination.next)
        setHasMore(hasNextPage)
        if (isInitial) setMessages(messagesList)
        else setMessages(prev => [...prev, ...messagesList])
      } else {
        showError('Failed to fetch messages')
      }
    } catch {
      showError('Failed to fetch messages')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchMessages(nextPage, false)
  }

  const handleMarkAsRead = async (messageId) => {
    try {
      const response = await messageAPI.markAsRead(messageId)
      if (response.success) {
        showSuccess('Message marked as read')
        setMessages(prev => prev.map(m =>
          m.id === messageId
            ? { ...m, is_read: true, read_count: (m.read_count || 0) + 1 }
            : m,
        ))
      } else {
        showError('Failed to mark message as read')
      }
    } catch {
      showError('Failed to mark message as read')
    }
  }

  const handlePlayVoice = (message) => {
    if (playingMessageId === message.id && audioPlayer) {
      if (isPlaying) { audioPlayer.pause(); setIsPlaying(false) }
      else { audioPlayer.play(); setIsPlaying(true) }
      return
    }
    if (audioPlayer) { audioPlayer.pause(); audioPlayer.currentTime = 0 }
    const audio = new Audio(message.voice_stream_url)
    setAudioPlayer(audio); setPlayingMessageId(message.id); setIsPlaying(true)
    audio.play().catch(() => {
      showError('Failed to play voice message')
      setAudioPlayer(null); setPlayingMessageId(null); setIsPlaying(false)
    })
    audio.onended = () => { setAudioPlayer(null); setPlayingMessageId(null); setIsPlaying(false) }
    audio.onpause = () => setIsPlaying(false)
    audio.onplay = () => setIsPlaying(true)
  }

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))
  const clearFilters = () => setFilters({ search: '', type: '', security_level: '', message_scope: '', is_read: '' })
  const hasActiveFilters = Object.values(filters).some(v => v !== '')
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineChatBubbleLeftRight}
        iconTone="brand"
        title={t('messages.title')}
        subtitle={t('messages.subtitle')}
        actions={
          <Button leadingIcon={HiOutlinePlusCircle} onClick={() => setIsCreateModalOpen(true)}>
            {t('messages.newMessage')}
          </Button>
        }
      />

      <Card padding="md">
        <div className="flex items-center gap-2 mb-3">
          <HiOutlineFunnel className="w-4 h-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">{t('common.filters')}</h2>
          {hasActiveFilters && <Badge tone="brand" size="sm">{activeFilterCount}</Badge>}
          {hasActiveFilters && (
            <Button variant="link" size="sm" onClick={clearFilters} className="ms-auto">
              {t('common.clearAll')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <Input
            leadingIcon={HiOutlineMagnifyingGlass}
            placeholder={t('messages.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            trailingAction={
              filters.search ? (
                <IconButton
                  icon={HiOutlineXMark}
                  aria-label={t('common.clearSearch')}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFilterChange('search', '')}
                />
              ) : null
            }
          />

          <Select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
            <option value="">{t('messages.filters.allTypes')}</option>
            <option value="text">{t('messages.filters.text')}</option>
            <option value="voice">{t('messages.filters.voice')}</option>
          </Select>

          <Select value={filters.security_level} onChange={(e) => handleFilterChange('security_level', e.target.value)}>
            <option value="">{t('messages.filters.allSecurityLevels')}</option>
            {securityLevelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.icon} {option.label.split(' - ')[0]}
              </option>
            ))}
          </Select>

          <Select value={filters.message_scope} onChange={(e) => handleFilterChange('message_scope', e.target.value)}>
            <option value="">{t('messages.filters.allScopes')}</option>
            <option value="direct">{t('messages.filters.direct')}</option>
            <option value="broadcast">{t('messages.filters.broadcast')}</option>
          </Select>

          <Select value={filters.is_read} onChange={(e) => handleFilterChange('is_read', e.target.value)}>
            <option value="">{t('messages.filters.allStatus')}</option>
            <option value="false">{t('messages.filters.unread')}</option>
            <option value="true">{t('messages.filters.read')}</option>
          </Select>
        </div>
      </Card>

      {loading ? (
        <Card padding="lg">
          <div className="flex flex-col items-center gap-3 py-8">
            <Spinner size="lg" />
            <p className="text-sm text-ink-muted">{t('messages.loadingMessages')}</p>
          </div>
        </Card>
      ) : messages.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={HiOutlineChatBubbleLeftRight}
            title={t('messages.noFound')}
            description={
              hasActiveFilters
                ? t('messages.noMatchFilters')
                : t('messages.getStarted')
            }
            action={
              !hasActiveFilters && (
                <Button leadingIcon={HiOutlinePlusCircle} onClick={() => setIsCreateModalOpen(true)}>
                  {t('messages.sendFirst')}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <MessageCard
              key={message.id}
              message={message}
              onMarkAsRead={handleMarkAsRead}
              onPlay={handlePlayVoice}
              playingMessageId={playingMessageId}
              isPlaying={isPlaying}
            />
          ))}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="secondary"
                loading={loadingMore}
                onClick={handleLoadMore}
              >
                {t('messages.loadMore')}
              </Button>
            </div>
          )}
        </div>
      )}

      <MessageCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false)
          setPage(1); setMessages([]); setHasMore(true)
          fetchMessages(1, true)
        }}
      />
    </div>
  )
}
